import {
  Button,
  Flex,
  FlexProps,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { queryClient } from '@shared/config';
import { links, maskDocument, maskTelephone } from '@shared/utils';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import {
  QUERY_KEY_GET_REQUEST_ALL_INFO,
  useCreateCheckout,
  useGetRequestAllInfo,
} from '../../hooks';

import { SummaryTermsModal } from './SummaryTermsModal';

const cardStyle: FlexProps = {
  flexDir: 'column',
  bg: 'brand.50',
  borderRadius: 'lg',
  p: 5,
};

export const SummaryPurchaseData = () => {
  const { t } = useTranslation('checkout/summary');
  const navigate = useNavigate();

  const {
    isOpen: isOpenTermsModal,
    onOpen: onOpenTermsModal,
    onClose: onCloseTermsModal,
  } = useDisclosure();

  const { data, isFindingAllInfo } = useGetRequestAllInfo();
  const { mutate: createCheckout, isLoading: isCreatingCheckout } =
    useCreateCheckout();
  const handleChangePersonalData = () => {
    navigate(links.checkout.data + `?finished`);
  };

  const handleCreateCheckout = () => {
    createCheckout(undefined, {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEY_GET_REQUEST_ALL_INFO);

        // remove all session infos
        sessionStorage.clear();

        navigate(
          `${links.checkout.success}?email=${data?.requestCustomers[0]?.email}`,
        );
      },
    });
  };

  if (isFindingAllInfo)
    return (
      <Flex
        gap={4}
        h="100dvh"
        maxH="calc(100dvh - 210px)"
        justifyContent="center"
        alignItems="center"
      >
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );

  return (
    <Flex flexDir="column" gap={4}>
      <Flex gap={4} h="100dvh" maxH="calc(100dvh - 162px)" overflowY="auto">
        <Flex flexDir="column" gap={4} w="full">
          <Text
            color="brand.800"
            fontSize="2xl"
            fontWeight={600}
            textAlign={{ base: 'center', lg: 'start' }}
          >
            {t('data')}
          </Text>
          <Flex {...cardStyle}>
            <Flex
              flexDir={{ base: 'column-reverse', lg: 'row' }}
              justifyContent={{ lg: 'space-between' }}
              alignItems={{ base: 'start', lg: 'center' }}
              mb={4}
            >
              <Text color="brand.800" fontSize="lg" fontWeight="semibold">
                {data?.requestCustomers[0]?.email}
              </Text>

              <Button
                onClick={handleChangePersonalData}
                variant="link"
                size="sm"
                colorScheme="brand"
              >
                {t('changePersonalData')}
              </Button>
            </Flex>

            <Text color="brand.800" fontSize="sm" fontWeight="medium">
              {data?.requestCustomers[0]?.documentType}:{' '}
              {maskDocument(data?.requestCustomers[0]?.documentNumber)}
            </Text>
            <Text color="brand.800" fontSize="sm" fontWeight="medium">
              {t('phone')}:{' '}
              {maskTelephone(data?.requestCustomers[0]?.phoneNumber)}
            </Text>
          </Flex>

          <Text
            color="brand.800"
            fontSize="2xl"
            fontWeight={600}
            textAlign={{ base: 'center', lg: 'start' }}
          >
            {t('paymentType')}
          </Text>

          <Flex {...cardStyle}>
            <Text color="brand.800" fontSize="lg" fontWeight="semibold">
              {t('cardInfo', {
                brand: data?.requestPayments[0]?.brand,
                cardNumber: data?.requestPayments[0]?.lastFourNumbers,
              })}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      <Flex justifyContent="space-between">
        <Button
          as={Link}
          to={links.checkout.payment}
          colorScheme="brand"
          variant="outline"
        >
          {t('common:previous')}
        </Button>

        <Button
          as={Link}
          to={links.checkout.summary}
          colorScheme="brand"
          w={{ base: 140, lg: 240 }}
          onClick={onOpenTermsModal}
          isLoading={isCreatingCheckout}
        >
          {t('common:continue')}
        </Button>
      </Flex>

      <SummaryTermsModal
        isOpen={isOpenTermsModal}
        onClose={onCloseTermsModal}
        onSubmit={handleCreateCheckout}
        isLoading={isCreatingCheckout}
      />
    </Flex>
  );
};
